import { buttonStyle } from 'components/buttons/buttonStyle';
import { RequiredAsterisk } from 'components/forms/inputs/rhf-inputs/InputWrapper';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { ModalFooter } from 'components/modals/ModalFooter';
import { ModalHeader } from 'components/modals/ModalHeader';
import { Button } from 'components/ui/button';
import { FieldError } from 'components/ui/field';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { useReducedMotion } from 'hooks/useReducedMotion';
import { useId, useRef, useState } from 'react';
import { useUpdateAssessmentMutation, useUpdateRelatedPartiesMutation } from 'store/api/client-identifications';
import { ClassifierData } from 'types/classifiers';
import {
    ClientIdentificationDto,
    ClientIdentificationPanelDto,
    ClientOwnerAnswer,
    RelatedPartiesDto,
    RelatedPartyGroupDto,
} from 'types/clientIdentification';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { assessmentUpdate } from '../assessmentUpdate';
import { classifierOptions } from '../identityForm';
import { DraftRow, draftToUpdate, MinContact, newDraftRow, toDraftRow } from '../relatedPartyRows';
import { AddPartyRow } from './AddPartyRow';
import { AnswerRadio } from './AnswerRadio';
import { BENEFICIAL_OWNER_ROLE, RemoveButton, RequiredMark, RowName } from './RelatedPartiesBlock';
import { successAlert } from 'utils/alert';

const OWNER_ANSWERS: ClientOwnerAnswer[] = ['HAS_OWNERS', 'NO_OWNERS'];

interface Props {
    contractId: number;
    assessment: ClientIdentificationDto;
    relatedParties: RelatedPartiesDto;
    panel: ClientIdentificationPanelDto;
    classifiers: Record<string, ClassifierData>;
    onClose: () => void;
}

/**
 * Edits every role's people at once and sends the whole list on Done, since the backend replaces the list. A refusal
 * shows as the API layer's toast and leaves the popup open with the edits.
 */
export function RelatedPartiesEditModal({
    contractId,
    assessment,
    relatedParties,
    panel,
    classifiers,
    onClose,
}: Props) {
    const [rows, setRows] = useState<DraftRow[]>(() =>
        [...relatedParties.groups, ...relatedParties.notAskedAtTier].flatMap((role) => role.rows.map(toDraftRow))
    );
    // oxlint-disable-next-line react-doctor/no-derived-useState -- the popup edits a copy, stored only on Done
    const [ownerAnswer, setOwnerAnswer] = useState(assessment.clientOwnerAnswer);
    const [addingTo, setAddingTo] = useState<string | null>(null);
    const [showsErrors, setShowsErrors] = useState(false);
    const newRowCount = useRef(0);
    const actingRoleTriggers = useRef(new Map<string, HTMLElement>());
    const reducedMotion = useReducedMotion();
    const [updateRelatedParties, { isLoading: isSavingRows }] = useUpdateRelatedPartiesMutation();
    const [updateAssessment, { isLoading: isSavingAnswer }] = useUpdateAssessmentMutation();
    const actingRoles = classifierOptions(classifiers['ACTING_FOR_CLIENT_ROLE']);

    const ownerGroup = panel.asksOwnerAnswer
        ? relatedParties.groups.find((group) => group.roleCode === BENEFICIAL_OWNER_ROLE)
        : undefined;
    const otherGroups = relatedParties.groups.filter((group) => group !== ownerGroup);
    const notAskedRoleCodes = new Set(relatedParties.notAskedAtTier.map((role) => role.roleCode));

    const rowsOf = (roleCode: string) => rows.filter((row) => row.roleCode === roleCode);
    const changeRow = (key: string, change: Partial<DraftRow>) =>
        setRows((current) => current.map((row) => (row.key === key ? { ...row, ...change } : row)));
    const removeRow = (key: string) => setRows((current) => current.filter((row) => row.key !== key));
    const addRow = (roleCode: string, entry: { contact?: MinContact; name?: string }) => {
        newRowCount.current += 1;
        const row = newDraftRow(`new-${newRowCount.current}`, roleCode, entry);
        setRows((current) => [...current, row]);
        setAddingTo(null);
    };

    const registerActingRoleTrigger = (key: string, trigger: HTMLElement) => {
        actingRoleTriggers.current.set(key, trigger);
        return () => {
            actingRoleTriggers.current.delete(key);
        };
    };

    const done = async () => {
        const firstWithoutActingRole = [ownerGroup, ...otherGroups]
            .flatMap((group) => (group?.takesActingRole ? rowsOf(group.roleCode) : []))
            .find((row) => !row.actingRoleCode);
        if (firstWithoutActingRole) {
            setShowsErrors(true);
            const trigger = actingRoleTriggers.current.get(firstWithoutActingRole.key);
            trigger?.focus({ preventScroll: true });
            trigger?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'center' });
            return;
        }
        // "No beneficial owner" leaves nobody in the role, so rows from an earlier "has owners" go with it.
        const keptRows =
            ownerGroup && ownerAnswer === 'NO_OWNERS'
                ? rows.filter((row) => row.roleCode !== ownerGroup.roleCode)
                : rows;
        try {
            await updateRelatedParties({ id: contractId, rows: keptRows.map(draftToUpdate) }).unwrap();
            if (ownerGroup && ownerAnswer !== assessment.clientOwnerAnswer) {
                await updateAssessment({
                    id: contractId,
                    ...assessmentUpdate(assessment, { clientOwnerAnswer: ownerAnswer }),
                }).unwrap();
            }
        } catch {
            // The API layer has shown the server's reason; the popup stays open with the edits.
            return;
        }
        successAlert(t('contracts.contract-updated'));
        onClose();
    };

    const groupEditor = (group: RelatedPartyGroupDto) => (
        <GroupEditor
            key={group.roleCode}
            group={group}
            rows={rowsOf(group.roleCode)}
            isAdding={addingTo === group.roleCode}
            onStartAdding={() => setAddingTo(group.roleCode)}
            onStopAdding={() => setAddingTo(null)}
            onAdd={(entry) => addRow(group.roleCode, entry)}
            onChangeRow={changeRow}
            onRemoveRow={removeRow}
            actingRoles={actingRoles.options}
            showsErrors={showsErrors}
            registerActingRoleTrigger={registerActingRoleTrigger}
        />
    );

    return (
        <>
            <ModalHeader title={t('client-identification.block.related-parties')} onCancel={onClose} />
            <div className="flex min-h-0 flex-col gap-6 overflow-y-auto p-6">
                {panel.asksOwnerAnswer ? (
                    <section className="flex flex-col gap-3">
                        <AnswerRadio<ClientOwnerAnswer>
                            label={t('client-identification.item.OWNER_ANSWER')}
                            required
                            value={ownerAnswer}
                            onChange={setOwnerAnswer}
                            options={OWNER_ANSWERS.map((answer) => ({
                                value: answer,
                                label: t(`client-identification.owner-answer.${answer}`),
                            }))}
                        />
                        <p className="text-caption text-foreground-secondary m-0">
                            {t('client-identification.owner-hint')}
                        </p>
                        {ownerGroup && ownerAnswer === 'HAS_OWNERS' ? groupEditor(ownerGroup) : null}
                    </section>
                ) : null}
                {otherGroups.map(groupEditor)}
                {rows.some((row) => notAskedRoleCodes.has(row.roleCode)) ? (
                    <section className="flex flex-col gap-2">
                        <h3 className="text-section m-0">{t('client-identification.not-asked-at-tier')}</h3>
                        <p className="text-caption text-foreground-secondary m-0">
                            {t('client-identification.not-asked-hint')}
                        </p>
                        {relatedParties.notAskedAtTier.flatMap((role) =>
                            rowsOf(role.roleCode).map((row) => (
                                <div key={row.key} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                    <RowName row={row} />
                                    <span className="text-caption text-foreground-secondary flex-auto">
                                        {role.name}
                                    </span>
                                    <RemoveButton
                                        name={row.linkedPartyName ?? row.name ?? ''}
                                        onRemove={() => removeRow(row.key)}
                                    />
                                </div>
                            ))
                        )}
                    </section>
                ) : null}
            </div>
            <ModalFooter
                onCancel={onClose}
                onSave={() => void done()}
                saveButtonTextId="done"
                disableSave={isSavingRows || isSavingAnswer}
            />
        </>
    );
}

interface GroupEditorProps {
    group: RelatedPartyGroupDto;
    rows: DraftRow[];
    isAdding: boolean;
    onStartAdding: () => void;
    onStopAdding: () => void;
    onAdd: (entry: { contact?: MinContact; name?: string }) => void;
    onChangeRow: (key: string, change: Partial<DraftRow>) => void;
    onRemoveRow: (key: string) => void;
    actingRoles: { label: string; value: string }[];
    showsErrors: boolean;
    registerActingRoleTrigger: (key: string, trigger: HTMLElement) => () => void;
}

function GroupEditor({
    group,
    rows,
    isAdding,
    onStartAdding,
    onStopAdding,
    onAdd,
    onChangeRow,
    onRemoveRow,
    actingRoles,
    showsErrors,
    registerActingRoleTrigger,
}: GroupEditorProps) {
    const headingId = useId();
    return (
        <section aria-labelledby={headingId} className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
                <h3 id={headingId} className="text-section m-0">
                    {group.namePlural}
                </h3>
                {group.isMandatory ? <RequiredMark /> : null}
                <span className="flex-auto" />
                {isAdding ? null : (
                    <Button
                        {...buttonStyle({ variant: 'text', size: 'small' })}
                        onClick={onStartAdding}
                        aria-label={t('client-identification.add-to-role', { role: group.namePlural })}
                    >
                        {t('add')}
                    </Button>
                )}
            </div>
            {rows.map((row) => (
                <RowEditor
                    key={row.key}
                    row={row}
                    group={group}
                    onChange={(change) => onChangeRow(row.key, change)}
                    onRemove={onRemoveRow}
                    actingRoles={actingRoles}
                    showsErrors={showsErrors}
                    registerActingRoleTrigger={registerActingRoleTrigger}
                />
            ))}
            {isAdding ? (
                <AddPartyRow
                    roleName={group.namePlural}
                    allowsTypedName={!group.isContactsOnly}
                    onAddContact={(contact) => onAdd({ contact })}
                    onAddName={(name) => onAdd({ name })}
                    onCancel={onStopAdding}
                />
            ) : null}
        </section>
    );
}

function RowEditor({
    row,
    group,
    onChange,
    onRemove,
    actingRoles,
    showsErrors,
    registerActingRoleTrigger,
}: {
    row: DraftRow;
    group: RelatedPartyGroupDto;
    onChange: (change: Partial<DraftRow>) => void;
    onRemove: (key: string) => void;
    actingRoles: { label: string; value: string }[];
    showsErrors: boolean;
    registerActingRoleTrigger: (key: string, trigger: HTMLElement) => () => void;
}) {
    const errorId = useId();
    const actingRoleLabelId = useId();
    const lacksActingRole = showsErrors && group.takesActingRole && !row.actingRoleCode;
    // The acting-role field carries a label above it; the other cells drop by that label's height to line up.
    const besideLabel = group.takesActingRole;
    return (
        <div className={cn('flex flex-wrap gap-x-3 gap-y-2', besideLabel ? 'items-start' : 'items-center')}>
            <div className={cn('flex min-w-0 flex-[1_1_12rem] flex-col gap-0.5', besideLabel && 'pt-6')}>
                <RowName row={row} />
                {!row.linkedPartyId && group.isContactsOnly ? (
                    <span className="text-caption text-foreground-secondary">
                        {t('client-identification.contacts-only-hint')}
                    </span>
                ) : null}
            </div>
            {group.takesActingRole ? (
                <div className="flex w-72 max-w-full flex-col gap-1">
                    <span
                        id={actingRoleLabelId}
                        className={cn(FIELD_LABEL_CLASS, lacksActingRole && 'text-destructive new:text-danger-ink')}
                    >
                        {t('client-identification.acting-role')}
                        <RequiredAsterisk />
                    </span>
                    <Select<string>
                        items={actingRoles}
                        value={row.actingRoleCode}
                        onValueChange={(code) => onChange({ actingRoleCode: code })}
                    >
                        <SelectTrigger
                            ref={(trigger) => (trigger ? registerActingRoleTrigger(row.key, trigger) : undefined)}
                            className="w-full"
                            aria-labelledby={actingRoleLabelId}
                            aria-required
                            aria-invalid={lacksActingRole || undefined}
                            aria-describedby={lacksActingRole ? errorId : undefined}
                        >
                            <SelectValue placeholder={t('client-identification.select-acting-role')} />
                        </SelectTrigger>
                        <SelectContent>
                            {actingRoles.map((role) => (
                                <SelectItem key={role.value} value={role.value}>
                                    {role.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {/* No alert role: the message is the trigger's description, read as focus moves there on Done. */}
                    {lacksActingRole ? (
                        <FieldError id={errorId} role={undefined}>
                            {t('client-identification.choose-acting-role')}
                        </FieldError>
                    ) : null}
                </div>
            ) : null}
            <div className={cn('flex min-w-0 flex-[1_1_12rem] flex-col', besideLabel && 'pt-5')}>
                <Input
                    value={row.note ?? ''}
                    placeholder={t('client-identification.note')}
                    aria-label={t('client-identification.note')}
                    onChange={(event) => onChange({ note: event.target.value || null })}
                />
            </div>
            <div className={cn(besideLabel && 'pt-5')}>
                <RemoveButton name={row.linkedPartyName ?? row.name ?? ''} onRemove={() => onRemove(row.key)} />
            </div>
        </div>
    );
}
